<template>
    <div id="business-list">
        <div class="mi-bl-header">
            <strong style="color: #1f1f1f; font-size: 14px;">{{ $t("metaInsights.selectBusiness") }}</strong>
            <span style="color: #6b7280; font-size: 12px;">{{ $t("metaInsights.selectBusinessDesc") }}</span>
        </div>
        <div class="mi-bl-list">
            <div
                v-for="biz in itemsWithPersonal"
                :key="biz.id"
                class="mi-bl-row"
                :class="{ 'mi-bl-selected': isSelected(biz) }"
                @click="select(biz)"
            >
                <div class="mi-bl-radio" :class="{ 'mi-bl-radio-checked': isSelected(biz) }">
                    <span class="mi-bl-dot" v-if="isSelected(biz)"></span>
                </div>
                <div class="mi-bl-icon" :class="{ 'mi-bl-icon-personal': biz.id === 'personal' }">
                    <i class="material-icons" v-if="biz.id === 'personal'">person</i>
                    <i class="material-icons" v-else>business</i>
                </div>
                <div class="mi-bl-info">
                    <div
                        class="mi-bl-name"
                        style="font-weight: 600; font-size: 14px; color: #1f1f1f; line-height: 1.4; word-break: break-word;"
                    >
                        {{ biz.name }}
                    </div>
                    <div class="mi-bl-meta">
                        <span
                            v-if="biz.id !== 'personal'"
                            class="mi-bl-badge"
                            style="background: #f0f0f0; color: #595959; font-family: ui-monospace, SFMono-Regular, monospace;"
                        >
                            {{ biz.id }}
                        </span>
                        <span
                            v-if="biz.verification_status"
                            class="mi-bl-badge"
                            :style="verificationBadgeStyle(biz.verification_status)"
                        >
                            <i class="material-icons" style="font-size: 12px;">verified</i>
                            {{ biz.verification_status }}
                        </span>
                        <span
                            v-if="biz.id === 'personal'"
                            class="mi-bl-badge"
                            style="background: #fff7e6; color: #d48806;"
                        >
                            {{ $t("metaInsights.personalAdAccounts") }}
                        </span>
                    </div>
                </div>
            </div>
            <div class="mi-bl-empty" v-if="!itemsWithPersonal || itemsWithPersonal.length === 0">
                {{ $t("metaInsights.noBusinesses") }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "BusinessList",
    props: {
        businesses: {
            type: Array,
            default: () => [],
        },
        value: {
            type: Object,
            default: null,
        },
        showPersonal: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            selectedId: this.value ? this.value.id : null,
        };
    },
    computed: {
        itemsWithPersonal() {
            const list = this.businesses ? this.businesses.slice() : [];
            if (this.showPersonal) {
                list.unshift({
                    id: "personal",
                    name: this.$t("metaInsights.personalBucket"),
                });
            }
            return list;
        },
    },
    methods: {
        isSelected(biz) {
            return this.selectedId === biz.id;
        },
        select(biz) {
            this.selectedId = biz.id;
            this.$emit("input", biz);
            this.$emit("change", biz);
        },
        verificationBadgeStyle(status) {
            const map = {
                verified: { bg: "#e6fffb", color: "#08979c" },
                not_verified: { bg: "#fff1f0", color: "#cf1322" },
                pending: { bg: "#fff7e6", color: "#d48806" },
            };
            const style = map[status] || { bg: "#f0f0f0", color: "#595959" };
            return "background: " + style.bg + "; color: " + style.color + ";";
        },
    },
    watch: {
        value(val) {
            this.selectedId = val ? val.id : null;
        },
    },
};
</script>

<style lang="scss" scoped>
#business-list {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .mi-bl-header {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .mi-bl-list {
        max-height: 360px;
        overflow-y: auto;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        background: #fff;
    }

    .mi-bl-row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 10px 12px;
        cursor: pointer;
        border-bottom: 1px solid #eee;
        transition: background 0.15s ease;

        &:hover {
            background: #f5f9ff;
        }

        &.mi-bl-selected {
            background: #eef6ff;
        }

        &:last-child {
            border-bottom: none;
        }
    }

    .mi-bl-radio {
        flex: 0 0 auto;
        width: 18px;
        height: 18px;
        border: 2px solid #c4c4c4;
        border-radius: 50%;
        margin-top: 2px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.mi-bl-radio-checked {
            border-color: #1f6feb;
        }

        .mi-bl-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #1f6feb;
        }
    }

    .mi-bl-icon {
        flex: 0 0 auto;
        width: 32px;
        height: 32px;
        border-radius: 6px;
        background: #eef4ff;
        color: #1f6feb;
        display: flex;
        align-items: center;
        justify-content: center;

        i {
            font-size: 18px;
        }

        &.mi-bl-icon-personal {
            background: #fff7e6;
            color: #d48806;
        }
    }

    .mi-bl-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 4px;
        min-width: 0;
    }

    .mi-bl-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .mi-bl-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        font-size: 11px;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: 10px;
        line-height: 1.6;
        white-space: nowrap;
    }

    .mi-bl-empty {
        padding: 20px;
        text-align: center;
        color: #6b7280;
    }
}
</style>
